import {gql} from '@apollo/client';
import React, {useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {
  CREATE_REQUEST_V2_MODAL_TYPE,
  REQUESTS_V2_ANALYTICS_ACTIONS,
} from 'constants/analytics-products/RequestsV2AnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import fetchSelectorsById from 'notebook/structured-tables/live-data/fetchSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTable,
  StructuredTableAdapter,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {UnknownStructuredTableAdapter} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import SelectTaskColumnMappingsModal, {
  SelectTaskColumnMappingsModalCreationType,
  TaskColumnMappingInfo,
} from 'processes/modals/SelectTaskColumnMappingsModal';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {getSourceColumnsAndTypeFromStructuredTable} from 'processes/tables/column-mapping/getSourceColumnsAndTypeFromStructuredTable';
import createEmptyTaskTableFromSchema from 'processes/tables/createEmptyTaskTableFromSchema';
import {
  computeItemsShortcutDisabledState,
  isStructuredTableShortcutDisabled,
} from 'processes/tables/shortcutDisabledState';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';
import bufferForUX from 'util/bufferForUX';

import CreateRequestV2SubmissionModal from '../modals/CreateRequestV2SubmissionModal';
import {CreateRequestV2SubmissionShortcutButtonQuery} from './__generated/CreateRequestV2SubmissionShortcutButtonQuery';

export type RequestV2DefinitionForCreateMenu = ExtractByTypename<
  CreateRequestV2SubmissionShortcutButtonQuery['allRequestV2Definitions'][number],
  'RequestV2DefinitionNode'
>;

export const BUTTON_TITLE = 'Create request';

export const CREATE_REQUEST_V2_SUBMISSION_SHORTCUT_BUTTON_FRAGMENT = gql`
  fragment CreateRequestV2SubmissionShortcutButtonFragment on RequestV2DefinitionNode {
    id
    name
    archiveData {
      archived
    }
    workflowTaskSchema {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
`;

export const CREATE_REQUEST_V2_SUBMISSION_SHORTCUT_BUTTON_QUERY = gql`
  query CreateRequestV2SubmissionShortcutButtonQuery @skipAudit @engComponent(name: WORKFLOWS) {
    allRequestV2Definitions {
      ...CreateRequestV2SubmissionShortcutButtonFragment
    }
  }
  ${CREATE_REQUEST_V2_SUBMISSION_SHORTCUT_BUTTON_FRAGMENT}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface CreateRequestV2SubmissionButtonFromTableProps {
  structuredTable: UnknownStructuredTable;
  structuredTableAdapter: UnknownStructuredTableAdapter;
  computedTable: Array<Array<EvaluatedCell>> | null;
}

interface CreateRequestV2SubmissionButtonFromItemsProps {
  disabled: boolean;
  items: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;
  actionOrigin: ActionOrigin;
  buttonStyle?: ButtonStyle;
}

type CreateRequestV2SubmissionShortcutButtonProps =
  | CreateRequestV2SubmissionButtonFromTableProps
  | CreateRequestV2SubmissionButtonFromItemsProps;

function isCreatingFromStructuredTable(
  props: CreateRequestV2SubmissionShortcutButtonProps
): props is CreateRequestV2SubmissionButtonFromTableProps {
  return (props as CreateRequestV2SubmissionButtonFromTableProps).structuredTable != null;
}

function isCreatingFromItems(
  props: CreateRequestV2SubmissionShortcutButtonProps
): props is CreateRequestV2SubmissionButtonFromItemsProps {
  return (props as CreateRequestV2SubmissionButtonFromItemsProps).items != null;
}

export function trackCreateRequestV2SubmissionShortcutOpened(actionOrigin: ActionOrigin): void {
  analytics.trackEvent(REQUESTS_V2_ANALYTICS_ACTIONS.CLICKED_REQUESTS_V2_CREATE_SHORTCUT, {
    action_origin: actionOrigin,
    action_method: ActionMethod.BUTTON_CLICK,
  });
}

interface SearchableRequestV2DefinitionMenuProps {
  requestV2Definitions: Array<RequestV2DefinitionForCreateMenu>;
  onSelect: (definition: RequestV2DefinitionForCreateMenu) => void;
}

function SearchableRequestV2DefinitionMenu(props: SearchableRequestV2DefinitionMenuProps): JSX.Element {
  return (
    <SearchableMenu
      height={Menu.HEIGHTS.MEDIUM}
      width={Menu.WIDTHS.MEDIUM}
      onSelect={props.onSelect}
      optionDatas={props.requestV2Definitions.map((definition) => ({
        value: definition,
        name: definition.name,
      }))}
      dropLeft={true}
    />
  );
}

export async function handleSelectRequestV2Definition(
  sourceColumns: Array<TaskColumnMappingInfo>,
  requestV2Definition: RequestV2DefinitionForCreateMenu,
  selectorsById: SchemaFieldSelectorsDict,
  actionOrigin: ActionOrigin,
  structuredTableAdapter?: StructuredTableAdapter<unknown, unknown>,
  structuredTable?: StructuredTable<unknown, unknown>
): Promise<void> {
  const analyticsEventGroupId = analytics.makeAnalyticsEventId();
  const taskSchema = requestV2Definition.workflowTaskSchema;
  trackCreateRequestV2SubmissionShortcutOpened(actionOrigin);
  assert(!isInaccessible(taskSchema), 'taskSchema is inaccessible');
  const taskTable = createEmptyTaskTableFromSchema({
    name: taskSchema.name,
    taskSchema,
    tableLocation: ProcessTableLocation.CREATE_MODAL,
    locked: false,
    selectorsById,
  });
  if (sourceColumns.length === 0) {
    analytics.trackEventWithModule(
      WORKFLOWS_ANALYTICS_ACTIONS.FAILED_COLUMN_MAPPING_TO_TASK_TABLE,
      ANALYTICS_MODULE.WORKFLOWS,
      {
        event_group_id: analyticsEventGroupId,
        action_origin: actionOrigin,
        modal_type: CREATE_REQUEST_V2_MODAL_TYPE,
      }
    );
    runModal(CreateRequestV2SubmissionModal, {
      actionOrigin,
      initialRequestV2DefinitionId: requestV2Definition.id,
      taskTableWithPropagatedContent: taskTable,
    });
  } else {
    const result = await runModal(SelectTaskColumnMappingsModal, {
      sourceColumns,
      taskSchema,
      taskTable,
      selectorsById,
      structuredTableAdapter,
      creationType: SelectTaskColumnMappingsModalCreationType.REQUEST_V2_SUBMISSION,
      structuredTable,
    });
    if (result != null && result.shouldRunCreateModal) {
      runModal(CreateRequestV2SubmissionModal, {
        actionOrigin,
        initialRequestV2DefinitionId: requestV2Definition.id,
        taskTableWithPropagatedContent: result.taskTable ?? taskTable,
      });
    }
  }
}

export default function CreateRequestV2SubmissionShortcutButton(
  props: CreateRequestV2SubmissionShortcutButtonProps
): JSX.Element {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [requestV2Definitions, setRequestV2Definitions] =
    useState<Array<RequestV2DefinitionForCreateMenu> | null>(null);
  const [selectorsById, setSelectorsById] = useState<SchemaFieldSelectorsDict | null>(null);

  const structuredTableDep = isCreatingFromStructuredTable(props) ? props.structuredTable : undefined;
  const computedTableDep = isCreatingFromStructuredTable(props) ? props.computedTable : undefined;

  const hasValidSourceColumns = useMemo(() => {
    if (structuredTableDep == null) {
      return true;
    }

    const adapter = getStructuredTableAdapters()[structuredTableDep.kind];
    const {sourceColumns} = getSourceColumnsAndTypeFromStructuredTable(
      structuredTableDep,
      computedTableDep ?? null,
      adapter
    );
    return sourceColumns.length > 0;
  }, [structuredTableDep, computedTableDep]);

  let isButtonDisabled: boolean;
  if (isCreatingFromStructuredTable(props)) {
    isButtonDisabled = isStructuredTableShortcutDisabled({
      structuredTable: props.structuredTable,
      hasValidSourceColumns,
    });
  } else if (isCreatingFromItems(props)) {
    isButtonDisabled = computeItemsShortcutDisabledState({
      itemsCount: props.items.length,
      disabledFlag: props.disabled,
    });
  } else {
    isButtonDisabled = true;
  }

  const buttonStyleProps = isCreatingFromStructuredTable(props)
    ? {size: ButtonSize.MINI, buttonStyle: ButtonStyle.TERTIARY_GRAY, className: 'u-mini-spacing-left'}
    : {
        size: ButtonSize.SMALL,
        buttonStyle: ButtonStyle.TERTIARY_WHITE,
      };

  const onSelect = (
    definition: RequestV2DefinitionForCreateMenu,
    selectorsById: SchemaFieldSelectorsDict
  ): void => {
    let actionOrigin: ActionOrigin;
    if (isCreatingFromStructuredTable(props)) {
      const adapter = getStructuredTableAdapters()[props.structuredTable.kind];
      const {sourceColumns} = getSourceColumnsAndTypeFromStructuredTable(
        props.structuredTable,
        props.computedTable,
        adapter
      );
      actionOrigin = structuredTableActionOrigin(props.structuredTable);
      handleSelectRequestV2Definition(
        sourceColumns,
        definition,
        selectorsById,
        actionOrigin,
        adapter,
        props.structuredTable
      );
    } else {
      actionOrigin = props.actionOrigin;
      handleSelectRequestV2Definition(
        [{columnName: 'Selected items from search', columnValues: props.items, hasValues: true}],
        definition,
        selectorsById,
        props.actionOrigin
      );
    }
  };

  return (
    <Tooltip title={BUTTON_TITLE}>
      <Dropdown
        menu={
          requestV2Definitions != null && selectorsById != null ? (
            <SearchableRequestV2DefinitionMenu
              requestV2Definitions={requestV2Definitions}
              onSelect={(definition: RequestV2DefinitionForCreateMenu) => {
                onSelect(definition, selectorsById);
              }}
            />
          ) : (
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
              <Option isDisabled={true}>Loading request types...</Option>
            </Menu>
          )
        }
        onToggle={async () => {
          setIsDropdownOpen(!isDropdownOpen);
          let allRequestV2DefinitionsFetch;
          let selectorsFetch;

          // Kick off fetches
          if (requestV2Definitions == null) {
            allRequestV2DefinitionsFetch = bufferForUX(
              doQuerySafe<CreateRequestV2SubmissionShortcutButtonQuery>({
                query: CREATE_REQUEST_V2_SUBMISSION_SHORTCUT_BUTTON_QUERY,
              })
            );
          }
          if (selectorsById == null) {
            selectorsFetch = bufferForUX(fetchSelectorsById(true));
          }

          // Process results
          if (allRequestV2DefinitionsFetch) {
            const result = await allRequestV2DefinitionsFetch;
            const requestV2Definitions = result.allRequestV2Definitions.filter(
              (requestV2Definition) =>
                !requestV2Definition.archiveData.archived &&
                !isInaccessible(requestV2Definition.workflowTaskSchema)
            );
            setRequestV2Definitions(requestV2Definitions);
          }
          if (selectorsFetch) {
            const selectors = await selectorsFetch;
            setSelectorsById(selectors);
          }
        }}
        isOpen={isDropdownOpen}
      >
        <Button
          {...buttonStyleProps}
          icon="request_v2_submission"
          disabled={isButtonDisabled}
          iconAltText={BUTTON_TITLE}
          isDropdown={true}
        />
      </Dropdown>
    </Tooltip>
  );
}
